/* eslint-disable react/jsx-no-bind, jsx-a11y/no-noninteractive-element-interactions */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  SyntheticEvent,
} from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { useLocation } from 'react-router-dom';

import { Moon, Sun } from 'lucide-react';

import rinspaceMark from '@/images/rinspace-mark-128.png';
import {
  RinspaceTopbarAnonymousActionBar,
  RinspacePhoneAuthDialog,
  RinspaceTopbarActionBar,
  RinspaceTopbarControls,
  RinspaceTopbarFrame,
} from '@/rinspace_shared/RinspaceTopbarFrame';
import type { RinspaceTopbarCompactMenu } from '@/rinspace_shared/RinspaceTopbarFrame';
import { AnimateButton } from 'mastodon/components/rinspace_animate/button';
import { Bell as AnimateBell } from 'mastodon/components/rinspace_animate/icons/bell';
import { BellRing as AnimateBellRing } from 'mastodon/components/rinspace_animate/icons/bell-ring';
import { Kanban as AnimateKanban } from 'mastodon/components/rinspace_animate/icons/kanban';
import { LogOut as AnimateLogOut } from 'mastodon/components/rinspace_animate/icons/log-out';
import { Plus as AnimatePlus } from 'mastodon/components/rinspace_animate/icons/plus';
import { Search as AnimateSearch } from 'mastodon/components/rinspace_animate/icons/search';
import { Settings as AnimateSettings } from 'mastodon/components/rinspace_animate/icons/settings';
import { Sparkles as AnimateSparkles } from 'mastodon/components/rinspace_animate/icons/sparkles';
import { User as AnimateUser } from 'mastodon/components/rinspace_animate/icons/user';
import { AnimateThemeToggler } from 'mastodon/components/rinspace_animate/theme_toggler';
import { useTheme } from 'mastodon/hooks/useTheme';
import { useIdentity } from 'mastodon/identity_context';
import { canViewAdminDashboard } from 'mastodon/permissions';
import { selectUnreadNotificationGroupsCount } from 'mastodon/selectors/notifications';
import {
  completeRinspacePhoneOtp,
  hasRinspaceIdentitySession,
  isMainlandPhone,
  normalizeMainlandPhone,
  RinspaceAuthError,
  sendRinspacePhoneOtp,
  startRinspaceSso,
} from 'mastodon/services/rinspace_auth';
import type { RinspaceOtpChallenge } from 'mastodon/services/rinspace_auth';
import { useAppSelector } from 'mastodon/store';
import { RINSPACE_LOGIN_REQUEST_EVENT } from 'mastodon/utils/rinspace_login';

const messages = defineMessages({
  brandName: { id: 'rinspace.world.brand_name', defaultMessage: 'Rinspace' },
  navigation: {
    id: 'rinspace.world.navigation',
    defaultMessage: 'Rinspace inner-world navigation',
  },
  flip: {
    id: 'rinspace.world.flip_to_outer',
    defaultMessage: 'Flip to the outer world',
  },
  home: {
    id: 'rinspace.world.inner_home',
    defaultMessage: 'Go to the inner-world home',
  },
  themeDark: {
    id: 'rinspace.world.theme_to_dark',
    defaultMessage: 'Switch to dark theme',
  },
  themeLight: {
    id: 'rinspace.world.theme_to_light',
    defaultMessage: 'Switch to light theme',
  },
  more: { id: 'rinspace.world.more', defaultMessage: 'More' },
  mine: { id: 'rinspace.world.mine', defaultMessage: 'Mine' },
  publish: { id: 'rinspace.world.publish', defaultMessage: 'Publish' },
  notifications: {
    id: 'rinspace.world.notifications',
    defaultMessage: 'Notifications',
  },
  administration: {
    id: 'navigation_bar.administration',
    defaultMessage: 'Administration',
  },
  account: { id: 'rinspace.world.account', defaultMessage: 'Your profile' },
  accountMenu: {
    id: 'rinspace.world.account_menu',
    defaultMessage: 'Account menu',
  },
  preferences: {
    id: 'navigation_bar.preferences',
    defaultMessage: 'Preferences',
  },
  signOut: { id: 'navigation_bar.sign_out', defaultMessage: 'Sign out' },
  signInOrRegister: {
    id: 'rinspace.world.sign_in_or_register',
    defaultMessage: 'Sign in / Register',
  },
  searchPlaceholder: {
    id: 'rinspace.world.search_placeholder',
    defaultMessage: 'Search people, posts, or tags',
  },
  searchLabel: { id: 'rinspace.world.search', defaultMessage: 'Search' },
  searchIndex: {
    id: 'rinspace.world.search_index',
    defaultMessage: 'Inner-world results',
  },
  searchLoading: {
    id: 'rinspace.world.search_loading',
    defaultMessage: 'Searching…',
  },
  searchEmpty: {
    id: 'rinspace.world.search_empty',
    defaultMessage: 'No matching people, posts, or tags',
  },
  searchFailed: {
    id: 'rinspace.world.search_failed',
    defaultMessage: 'Search is temporarily unavailable. Try again.',
  },
  searchPeople: {
    id: 'rinspace.world.search_people',
    defaultMessage: 'People',
  },
  searchPosts: {
    id: 'rinspace.world.search_posts',
    defaultMessage: 'Posts',
  },
  searchTags: {
    id: 'rinspace.world.search_tags',
    defaultMessage: 'Tags',
  },
  authTitle: {
    id: 'rinspace.auth.title',
    defaultMessage: 'Sign in / Register',
  },
  authPhone: { id: 'rinspace.auth.phone', defaultMessage: 'Phone number' },
  authPhonePlaceholder: {
    id: 'rinspace.auth.phone_placeholder',
    defaultMessage: 'Enter your phone number',
  },
  authCode: {
    id: 'rinspace.auth.code',
    defaultMessage: 'Verification code',
  },
  authCodePlaceholder: {
    id: 'rinspace.auth.code_placeholder',
    defaultMessage: 'SMS verification code',
  },
  authSend: {
    id: 'rinspace.auth.send_code',
    defaultMessage: 'Send code',
  },
  authComplete: {
    id: 'rinspace.auth.complete',
    defaultMessage: 'Complete sign-in',
  },
  authProcessing: {
    id: 'rinspace.auth.processing',
    defaultMessage: 'Processing…',
  },
  authChangePhone: {
    id: 'rinspace.auth.change_phone',
    defaultMessage: 'Use another phone number',
  },
  authClose: {
    id: 'rinspace.auth.close',
    defaultMessage: 'Close sign-in window',
  },
  authPhoneInvalid: {
    id: 'rinspace.auth.phone_invalid',
    defaultMessage: 'Enter an 11-digit mainland China phone number.',
  },
  authCodeInvalid: {
    id: 'rinspace.auth.code_invalid',
    defaultMessage: 'Enter the SMS verification code.',
  },
  authExisting: {
    id: 'rinspace.auth.existing',
    defaultMessage: 'Code sent. Enter it to sign in.',
  },
  authNew: {
    id: 'rinspace.auth.new',
    defaultMessage:
      'Code sent. A new account will be created for this phone number.',
  },
  authTimeout: {
    id: 'rinspace.auth.timeout',
    defaultMessage: 'The sign-in request timed out. Please try again.',
  },
  authUnavailable: {
    id: 'rinspace.auth.unavailable',
    defaultMessage: 'Rinspace sign-in is temporarily unavailable.',
  },
  authFailed: {
    id: 'rinspace.auth.failed',
    defaultMessage: 'Sign-in was not completed. Please try again.',
  },
});

interface SearchAccount {
  id: string;
  acct: string;
  display_name: string;
}

interface SearchStatus {
  id: string;
  content: string;
  account: SearchAccount;
}

interface SearchTag {
  name: string;
}

interface SearchResponse {
  accounts?: SearchAccount[];
  statuses?: SearchStatus[];
  hashtags?: SearchTag[];
}

type SearchResult =
  | {
      key: string;
      kind: 'account';
      title: string;
      detail: string;
      href: string;
    }
  | { key: string; kind: 'status'; title: string; detail: string; href: string }
  | { key: string; kind: 'tag'; title: string; detail: string; href: string };

function plainStatus(value: string) {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

function toSearchResults(payload: SearchResponse): SearchResult[] {
  return [
    ...(payload.accounts ?? []).map((account) => ({
      key: `account-${account.id}`,
      kind: 'account' as const,
      title: account.display_name || account.acct,
      detail: `@${account.acct}`,
      href: innerHref(`/@${encodeURIComponent(account.acct)}`, '', ''),
    })),
    ...(payload.statuses ?? []).map((status) => ({
      key: `status-${status.id}`,
      kind: 'status' as const,
      title: plainStatus(status.content) || `@${status.account.acct}`,
      detail: `@${status.account.acct}`,
      href: `/p/${encodeURIComponent(status.id)}`,
    })),
    ...(payload.hashtags ?? []).map((tag) => ({
      key: `tag-${tag.name}`,
      kind: 'tag' as const,
      title: `#${tag.name}`,
      detail: '',
      href: innerHref(`/tags/${encodeURIComponent(tag.name)}`, '', ''),
    })),
  ].slice(0, 6);
}

const RinspaceInnerSearch: React.FC = () => {
  const intl = useIntl();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const form = useRef<HTMLFormElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const trimmed = query.trim();
  const resultKind = {
    account: intl.formatMessage(messages.searchPeople),
    status: intl.formatMessage(messages.searchPosts),
    tag: intl.formatMessage(messages.searchTags),
  };

  useEffect(() => {
    if (trimmed.length < 2) {
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(false);
      const params = new URLSearchParams({
        q: trimmed,
        resolve: 'false',
        limit: '4',
      });
      void fetch(`/api/v2/search?${params.toString()}`, {
        credentials: 'same-origin',
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) throw new Error(`search_${response.status}`);
          return (await response.json()) as SearchResponse;
        })
        .then((payload) => {
          setResults(toSearchResults(payload));
        })
        .catch((reason: unknown) => {
          if (
            !(reason instanceof DOMException && reason.name === 'AbortError')
          ) {
            setResults([]);
            setError(true);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 240);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [trimmed]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !form.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', close, true);
    return () => {
      document.removeEventListener('pointerdown', close, true);
    };
  }, [open]);

  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmed) {
      input.current?.focus();
      setOpen(true);
      return;
    }
    window.location.assign(
      innerHref('/search', `?q=${encodeURIComponent(trimmed)}`, ''),
    );
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      input.current?.blur();
    }
  };

  return (
    <form
      ref={form}
      className={open ? 'topbar-search mobile-search-open' : 'topbar-search'}
      role='search'
      onSubmit={submit}
      onKeyDown={onKeyDown}
    >
      <input
        ref={input}
        value={query}
        maxLength={60}
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        aria-label={intl.formatMessage(messages.searchLabel)}
        onFocus={() => {
          setOpen(true);
        }}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
        }}
      />
      <AnimateButton
        unstyled
        type='submit'
        title={intl.formatMessage(messages.searchLabel)}
        aria-label={intl.formatMessage(messages.searchLabel)}
      >
        <AnimateSearch animateOnHover size={16} />
      </AnimateButton>
      {open && trimmed.length >= 2 ? (
        <div className='topbar-search-preview' aria-live='polite'>
          <div className='topbar-search-preview-head'>
            <span>{intl.formatMessage(messages.searchIndex)}</span>
            <span>
              {loading
                ? intl.formatMessage(messages.searchLoading)
                : results.length}
            </span>
          </div>
          {error ? (
            <p className='topbar-search-preview-note'>
              {intl.formatMessage(messages.searchFailed)}
            </p>
          ) : null}
          {!error && !loading && results.length === 0 ? (
            <p className='topbar-search-preview-note'>
              {intl.formatMessage(messages.searchEmpty)}
            </p>
          ) : null}
          {results.map((result) => (
            <a
              className='topbar-search-result'
              href={result.href}
              key={result.key}
            >
              <span>{resultKind[result.kind]}</span>
              <strong>{result.title}</strong>
              <em>{result.detail}</em>
            </a>
          ))}
        </div>
      ) : null}
    </form>
  );
};

export function innerHref(pathname: string, search: string, hash: string) {
  const url = new URL(`${pathname}${search}${hash}`, window.location.origin);
  if (!url.pathname.startsWith('/p/')) url.searchParams.set('world', 'inner');
  return `${url.pathname}${url.search}${url.hash}`;
}

function innerReturnHref(pathname: string, search: string, hash: string) {
  const url = new URL(`${pathname}${search}${hash}`, window.location.origin);
  url.searchParams.delete('rinspace_login');
  url.searchParams.delete('rinspace_return_to');
  return innerHref(
    url.pathname,
    url.search,
    url.hash === '#login' ? '' : url.hash,
  );
}

function recoveredReturnHref(pathname: string, search: string, hash: string) {
  const current = new URL(
    `${pathname}${search}${hash}`,
    window.location.origin,
  );
  const requested = current.searchParams.get('rinspace_return_to');
  if (requested?.startsWith('/') && !requested.startsWith('//')) {
    const target = new URL(requested, window.location.origin);
    if (target.origin === window.location.origin) {
      return innerReturnHref(target.pathname, target.search, target.hash);
    }
  }
  return innerReturnHref(pathname, search, hash);
}

function submitSignOut() {
  const csrfParam = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-param"]',
  );
  const csrfToken = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  );
  if (!csrfParam?.content || !csrfToken?.content) return;

  const form = document.createElement('form');
  form.method = 'post';
  form.action = '/auth/sign_out';
  form.hidden = true;
  const fields: [string, string][] = [
    [csrfParam.content, csrfToken.content],
    ['_method', 'delete'],
  ];
  for (const [name, value] of fields) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.requestSubmit();
}

interface LoginDialogProps {
  open: boolean;
  returnTo: string;
  initialError: string;
  onClose: () => void;
}

const RinspaceLoginDialog: React.FC<LoginDialogProps> = ({
  open,
  returnTo,
  initialError,
  onClose,
}) => {
  const intl = useIntl();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<RinspaceOtpChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);
  const [status, setStatus] = useState('');

  const friendlyError = useCallback(
    (reason: unknown) => {
      if (reason instanceof RinspaceAuthError) {
        if (reason.code === 'timeout') {
          return intl.formatMessage(messages.authTimeout);
        }
        if (reason.code === 'unavailable' || reason.code === 'storage') {
          return intl.formatMessage(messages.authUnavailable);
        }
      }
      return intl.formatMessage(messages.authFailed);
    },
    [intl],
  );

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedPhone = normalizeMainlandPhone(phone);
    if (!isMainlandPhone(normalizedPhone)) {
      setError(intl.formatMessage(messages.authPhoneInvalid));
      return;
    }
    if (challenge && !/^\d{4,8}$/.test(code.trim())) {
      setError(intl.formatMessage(messages.authCodeInvalid));
      return;
    }

    setBusy(true);
    setError('');
    setStatus('');
    try {
      if (!challenge) {
        const nextChallenge = await sendRinspacePhoneOtp(normalizedPhone);
        setChallenge(nextChallenge);
        setPhone(normalizedPhone);
        setStatus(
          intl.formatMessage(
            nextChallenge.isUser ? messages.authExisting : messages.authNew,
          ),
        );
      } else {
        await completeRinspacePhoneOtp(challenge, code.trim());
        startRinspaceSso(returnTo);
      }
    } catch (reason) {
      setError(friendlyError(reason));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    void submit(event);
  };

  return (
    <RinspacePhoneAuthDialog
      open={open}
      busy={busy}
      phone={phone}
      code={code}
      challenge={Boolean(challenge)}
      error={error}
      status={status}
      labels={{
        title: intl.formatMessage(messages.authTitle),
        close: intl.formatMessage(messages.authClose),
        phone: intl.formatMessage(messages.authPhone),
        phonePlaceholder: intl.formatMessage(messages.authPhonePlaceholder),
        code: intl.formatMessage(messages.authCode),
        codePlaceholder: intl.formatMessage(messages.authCodePlaceholder),
        changePhone: intl.formatMessage(messages.authChangePhone),
        processing: intl.formatMessage(messages.authProcessing),
        complete: intl.formatMessage(messages.authComplete),
        sendCode: intl.formatMessage(messages.authSend),
      }}
      onClose={onClose}
      onPhoneChange={setPhone}
      onCodeChange={setCode}
      onChangePhone={() => {
        setChallenge(null);
        setCode('');
        setStatus('');
        setError('');
      }}
      onSubmit={handleSubmit}
    />
  );
};

interface RinspaceMoreMenuProps {
  open: boolean;
  trigger: ReactNode;
  children: ReactNode;
  onToggle: () => void;
}

const RinspaceMoreMenu: React.FC<RinspaceMoreMenuProps> = ({
  open,
  trigger,
  children,
  onToggle,
}) => {
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !menu.current?.contains(event.target)
      ) {
        onToggle();
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onToggle();
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer, true);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [onToggle, open]);

  return (
    <div ref={menu} className='topbar-more-menu'>
      {trigger}
      {open ? (
        <div className='rin-more-menu' role='menu'>
          {children}
        </div>
      ) : null}
    </div>
  );
};

export const RinspaceWorldTopbar: React.FC<{
  avatar?: string;
  displayName?: string;
  username?: string;
}> = ({ avatar, displayName, username }) => {
  const intl = useIntl();
  const location = useLocation();
  const theme = useTheme();
  const { signedIn, permissions } = useIdentity();
  const unreadNotifications = useAppSelector(
    selectUnreadNotificationGroupsCount,
  );
  const loginRequested = useMemo(() => {
    const recovery = new URLSearchParams(location.search).get('rinspace_login');
    return !signedIn && (location.hash === '#login' || recovery === '1');
  }, [location.hash, location.search, signedIn]);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const loginTrigger = useRef<HTMLButtonElement>(null);
  const loginRequestedRef = useRef(false);
  const toggleMore = useCallback(() => {
    setMoreOpen((current) => !current);
  }, []);
  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    window.requestAnimationFrame(() => loginTrigger.current?.focus());
  }, []);
  const returnTo = useMemo(
    () =>
      recoveredReturnHref(location.pathname, location.search, location.hash),
    [location.hash, location.pathname, location.search],
  );

  useEffect(() => {
    const stored = window.localStorage.getItem('rinspace-inner-color-scheme');
    if (stored === 'light' || stored === 'dark') {
      document.documentElement.dataset.colorScheme = stored;
      document.documentElement.dataset.systemTheme = 'false';
    }
  }, []);

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    document.documentElement.dataset.colorScheme = next;
    document.documentElement.dataset.systemTheme = 'false';
    window.localStorage.setItem('rinspace-inner-color-scheme', next);
  };

  const beginLogin = useCallback(async () => {
    if (loginBusy) return;
    setLoginBusy(true);
    setLoginError('');
    try {
      // The outer world owns the Rinspace identity session. Whenever that
      // session can answer for this visitor the inner world must hand off to
      // the shared OIDC entry point instead of asking for a second login.
      if (await hasRinspaceIdentitySession()) {
        startRinspaceSso(returnTo);
        return;
      }
      setLoginOpen(true);
    } catch (reason) {
      setLoginError(
        reason instanceof RinspaceAuthError && reason.code === 'timeout'
          ? intl.formatMessage(messages.authTimeout)
          : intl.formatMessage(messages.authFailed),
      );
      setLoginOpen(true);
    } finally {
      setLoginBusy(false);
    }
  }, [intl, loginBusy, returnTo]);

  useEffect(() => {
    if (!loginRequested || loginRequestedRef.current) return;
    loginRequestedRef.current = true;
    void beginLogin();
  }, [beginLogin, loginRequested]);

  useEffect(() => {
    const openUnifiedLogin = (event: Event) => {
      event.preventDefault();
      void beginLogin();
    };
    window.addEventListener(RINSPACE_LOGIN_REQUEST_EVENT, openUnifiedLogin);
    return () => {
      window.removeEventListener(
        RINSPACE_LOGIN_REQUEST_EVENT,
        openUnifiedLogin,
      );
    };
  }, [beginLogin]);

  const canAdmin = canViewAdminDashboard(permissions);
  const accountName = displayName?.trim()
    ? displayName.trim()
    : (username ?? '');
  const themeControl = (
    <AnimateThemeToggler
      className='topbar-pill'
      label={intl.formatMessage(
        theme === 'light' ? messages.themeDark : messages.themeLight,
      )}
      resolved={theme}
      onToggle={toggleTheme}
    />
  );
  const compactMenu: RinspaceTopbarCompactMenu = {
    label: intl.formatMessage(messages.more),
    expanded: moreOpen,
    onSelect: toggleMore,
    render: (trigger) => (
      <RinspaceMoreMenu open={moreOpen} onToggle={toggleMore} trigger={trigger}>
        {signedIn ? (
          <>
            <a
              role='menuitem'
              href={innerHref('/home', '', '')}
              onClick={() => {
                setMoreOpen(false);
              }}
            >
              <AnimateSparkles animateOnHover size={16} />
              {intl.formatMessage(messages.mine)}
            </a>
            <a
              role='menuitem'
              href={innerHref('/publish', '', '')}
              onClick={() => {
                setMoreOpen(false);
              }}
            >
              <AnimatePlus animateOnHover size={16} />
              {intl.formatMessage(messages.publish)}
            </a>
            <a
              role='menuitem'
              href={innerHref('/notifications', '', '')}
              onClick={() => {
                setMoreOpen(false);
              }}
            >
              {unreadNotifications > 0 ? (
                <AnimateBellRing animateOnHover size={16} />
              ) : (
                <AnimateBell animateOnHover size={16} />
              )}
              {intl.formatMessage(messages.notifications)}
              {unreadNotifications > 0 ? (
                <span className='topbar-menu-count'>{unreadNotifications}</span>
              ) : null}
            </a>
            <button
              type='button'
              role='menuitem'
              onClick={() => {
                toggleTheme();
                setMoreOpen(false);
              }}
            >
              {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
              {intl.formatMessage(
                theme === 'light' ? messages.themeDark : messages.themeLight,
              )}
            </button>
            {canAdmin ? (
              <a
                role='menuitem'
                href={innerHref('/admin', '', '')}
                onClick={() => {
                  setMoreOpen(false);
                }}
              >
                <AnimateKanban animateOnHover size={16} />
                {intl.formatMessage(messages.administration)}
              </a>
            ) : null}
          </>
        ) : (
          <button
            type='button'
            role='menuitem'
            onClick={() => {
              toggleTheme();
              setMoreOpen(false);
            }}
          >
            {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
            {intl.formatMessage(
              theme === 'light' ? messages.themeDark : messages.themeLight,
            )}
          </button>
        )}
      </RinspaceMoreMenu>
    ),
  };

  return (
    <>
      <RinspaceTopbarFrame
        sessionState={signedIn ? 'authenticated' : 'anonymous'}
        logoSrc={rinspaceMark}
        brandName={intl.formatMessage(messages.brandName)}
        flipHref='/'
        homeHref={innerHref('/explore', '', '')}
        flipLabel={intl.formatMessage(messages.flip)}
        homeLabel={intl.formatMessage(messages.home)}
      >
        <RinspaceTopbarControls
          navigationLabel={intl.formatMessage(messages.navigation)}
          search={<RinspaceInnerSearch />}
        >
          {signedIn ? (
            <RinspaceTopbarActionBar
              themeControl={themeControl}
              compactMenu={compactMenu}
              primary={{
                href: innerHref('/home', '', ''),
                label: intl.formatMessage(messages.mine),
              }}
              publishing={{
                label: intl.formatMessage(messages.publish),
                onSelect: () => {
                  window.location.href = innerHref('/publish', '', '');
                },
              }}
              notifications={{
                href: innerHref('/notifications', '', ''),
                label: intl.formatMessage(messages.notifications),
                count: unreadNotifications,
              }}
              administration={
                canAdmin
                  ? {
                      href: innerHref('/admin', '', ''),
                      label: intl.formatMessage(messages.administration),
                    }
                  : undefined
              }
              nativeTitles
              renderAccount={(chevron) =>
                username ? (
                  <details className='account-menu'>
                    <summary
                      className='account-menu-trigger'
                      aria-label={intl.formatMessage(messages.accountMenu)}
                    >
                      <span className='avatar-name'>
                        <span className='avatar-name-mark' aria-hidden='true'>
                          {avatar ? (
                            <img src={avatar} alt='' />
                          ) : (
                            username.slice(0, 1).toUpperCase()
                          )}
                        </span>
                        <span className='avatar-name-text'>{accountName}</span>
                      </span>
                      {chevron}
                    </summary>
                    <div className='rin-account-menu' role='menu'>
                      <a
                        role='menuitem'
                        href={innerHref(
                          `/@${encodeURIComponent(username)}`,
                          '',
                          '',
                        )}
                      >
                        <AnimateUser animateOnHover size={16} />
                        {intl.formatMessage(messages.account)}
                      </a>
                      <a
                        role='menuitem'
                        href={innerHref(
                          '/settings/preferences/appearance',
                          '',
                          '',
                        )}
                      >
                        <AnimateSettings animateOnHover size={16} />
                        {intl.formatMessage(messages.preferences)}
                      </a>
                      <button
                        type='button'
                        role='menuitem'
                        onClick={submitSignOut}
                      >
                        <AnimateLogOut animateOnHover size={16} />
                        {intl.formatMessage(messages.signOut)}
                      </button>
                    </div>
                  </details>
                ) : null
              }
            />
          ) : (
            <RinspaceTopbarAnonymousActionBar
              themeControl={themeControl}
              compactMenu={compactMenu}
              authentication={{
                label: intl.formatMessage(messages.signInOrRegister),
                onSelect: () => void beginLogin(),
                disabled: loginBusy,
                buttonRef: loginTrigger,
              }}
            />
          )}
        </RinspaceTopbarControls>
      </RinspaceTopbarFrame>
      {loginOpen ? (
        <RinspaceLoginDialog
          open
          returnTo={returnTo}
          initialError={loginError}
          onClose={closeLogin}
        />
      ) : null}
    </>
  );
};
